import { Checkbox, FormControl, Grid, MenuItem, Select } from "@mui/material";
import TextField from "@mui/material/TextField";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import * as React from 'react';
import ReactQuill from "react-quill-new";
import DOMPurify from "dompurify";
import AccordionTab from "../../../AccordianTab";
import { updateEuropeLeaseProjectDetail } from "../../../redux/slices/europeLeaseProjectDetailSlice";
import { updateErrorFields } from "../../../redux/slices/submitFormErrorSlice";
import "react-quill-new/dist/quill.snow.css";

const EuropeLeaseProjectDetails: React.FC<Record<any, any>> = (props) => {
    const dispatch = useAppDispatch();

    const formData = useAppSelector((state) => state.getFormData.formData);
    const submitErrorModel = useAppSelector((state) => state.submitFormError);
    const europeLeaseProjectDetailData = useAppSelector((state) => state.europeLeaseProjectDetail.data);

    const [OFACCheckbox, setOFACCheckbox] = React.useState<boolean>(false);
    const [businessPlanSelect, setBusinessPlanSelect] = React.useState('');
    const [valuationSelect, setValuationSelect] = React.useState('');

    React.useEffect(() => {
        if (formData !== null) {
            setOFACCheckbox(formData?.ofacDocCertified);
            setBusinessPlanSelect(formData?.euIsInLineWithBusinessPlan === null || formData?.euIsInLineWithBusinessPlan === undefined ? "" : formData?.euIsInLineWithBusinessPlan);
            setValuationSelect(formData?.euIsInLineWithValuation === null || formData?.euIsInLineWithValuation === undefined ? "" : formData?.euIsInLineWithValuation);
            dispatch(updateEuropeLeaseProjectDetail({
                ...europeLeaseProjectDetailData,
                leaseName: formData?.euLeaseName,
                tenantName: formData?.euTenantName,
                demisedPremises: formData?.euDemisedPremises,
                landlord: formData?.euLandlord,
                leaseTerm: formData?.euLeaseTerm,
                breakOptions: formData?.euBreakOptions,
                act1954: formData?.eu1954Act,
                rent: formData?.euRent,
                rentFree: formData?.euRentFree,
                delivery: formData?.euDelivery,
                deposit: formData?.euDeposit,
                serviceCharge: formData?.euServiceCharge,
                isInLineWithBusinessPlan: formData?.euIsInLineWithBusinessPlan,
                businessPlanComment: formData?.euBusinessPlanComment,
                isInLineWithValuation: formData?.euIsInLineWithValuation,
                valuationComment: formData?.euValuationComment,
                detailedLeaseDescription: formData?.euDetailedLeaseDescription,
                ofacDoc: formData?.ofacDocCertified
            }));
        }
    }, [formData]);

    const updateField = (field: string, value: any, clearedErrors?: Record<string, boolean>) => {
        dispatch(updateEuropeLeaseProjectDetail({ ...europeLeaseProjectDetailData, [field]: value }));
        if (clearedErrors) {
            dispatch(updateErrorFields({ ...submitErrorModel.data, ...clearedErrors }));
        }
    };

    const handleBusinessPlanChange = (event: any) => {
        const value = event.target.value;
        setBusinessPlanSelect(value);
        dispatch(updateEuropeLeaseProjectDetail({
            ...europeLeaseProjectDetailData,
            isInLineWithBusinessPlan: value,
            businessPlanComment: value === "Yes" ? "" : europeLeaseProjectDetailData.businessPlanComment
        }));
        dispatch(updateErrorFields({ ...submitErrorModel.data, euIsInLineWithBusinessPlan: false, euBusinessPlanComment: false }));
    };

    const handleValuationChange = (event: any) => {
        const value = event.target.value;
        setValuationSelect(value);
        dispatch(updateEuropeLeaseProjectDetail({
            ...europeLeaseProjectDetailData,
            isInLineWithValuation: value,
            valuationComment: value === "Yes" ? "" : europeLeaseProjectDetailData.valuationComment
        }));
        dispatch(updateErrorFields({ ...submitErrorModel.data, euIsInLineWithValuation: false, euValuationComment: false }));
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOFACCheckbox(event.target.checked);
        updateField('ofacDoc', event.target.checked, { euOFAC: false });
    };

    const handleDetailedDescriptionChange = (value: string) => {
        const sanitized = DOMPurify.sanitize(value);
        updateField('detailedLeaseDescription', sanitized, { euDetailedLeaseDescription: false });
    };

    return (
        <AccordionTab title={'Lease Detail'} children={
            <>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Lease Name</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.leaseName}
                                error={submitErrorModel.data.euLeaseName}
                                onChange={(event) => updateField('leaseName', event.target.value, { euLeaseName: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Tenant</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.tenantName}
                                error={submitErrorModel.data.euTenantName}
                                onChange={(event) => updateField('tenantName', event.target.value, { euTenantName: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Demised Premises</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.demisedPremises}
                                error={submitErrorModel.data.euDemisedPremises}
                                onChange={(event) => updateField('demisedPremises', event.target.value, { euDemisedPremises: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Landlord</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.landlord}
                                error={submitErrorModel.data.euLandlord}
                                onChange={(event) => updateField('landlord', event.target.value, { euLandlord: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Lease Term</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth multiline minRows={2} maxRows={5} value={europeLeaseProjectDetailData.leaseTerm}
                                error={submitErrorModel.data.euLeaseTerm}
                                onChange={(event) => updateField('leaseTerm', event.target.value, { euLeaseTerm: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Break Options</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth multiline minRows={2} maxRows={5} value={europeLeaseProjectDetailData.breakOptions}
                                error={submitErrorModel.data.euBreakOptions}
                                onChange={(event) => updateField('breakOptions', event.target.value, { euBreakOptions: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>1954 Act</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.act1954}
                                error={submitErrorModel.data.euAct1954}
                                onChange={(event) => updateField('act1954', event.target.value, { euAct1954: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Rent</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.rent}
                                error={submitErrorModel.data.euRent}
                                onChange={(event) => updateField('rent', event.target.value, { euRent: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Rent Free</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.rentFree}
                                error={submitErrorModel.data.euRentFree}
                                onChange={(event) => updateField('rentFree', event.target.value, { euRentFree: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Delivery</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.delivery}
                                error={submitErrorModel.data.euDelivery}
                                onChange={(event) => updateField('delivery', event.target.value, { euDelivery: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Deposit</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.deposit}
                                error={submitErrorModel.data.euDeposit}
                                onChange={(event) => updateField('deposit', event.target.value, { euDeposit: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Service Charge</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth value={europeLeaseProjectDetailData.serviceCharge}
                                error={submitErrorModel.data.euServiceCharge}
                                onChange={(event) => updateField('serviceCharge', event.target.value, { euServiceCharge: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Is this Lease in-line with the Business Plan?</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <FormControl sx={{ m: 1, minWidth: 120, margin: 0, width: "100%" }} size="small">
                                <Select
                                    value={businessPlanSelect}
                                    onChange={handleBusinessPlanChange}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    error={submitErrorModel.data.euIsInLineWithBusinessPlan}
                                    disabled={!formData?.isFormEditable}
                                >
                                    <MenuItem value="">Select</MenuItem>
                                    <MenuItem value={"Yes"}>Yes</MenuItem>
                                    <MenuItem value={"No"}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {businessPlanSelect === "No" ? <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Business Plan Comment</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth multiline minRows={1} maxRows={3} value={europeLeaseProjectDetailData.businessPlanComment}
                                error={submitErrorModel.data.euBusinessPlanComment}
                                onChange={(event) => updateField('businessPlanComment', event.target.value, { euBusinessPlanComment: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid> : <Grid item xs={12} md={6} className="grid-hide"></Grid>}

                    <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Is this Lease in-line with the Valuation?</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <FormControl sx={{ m: 1, minWidth: 120, margin: 0, width: "100%" }} size="small">
                                <Select
                                    value={valuationSelect}
                                    onChange={handleValuationChange}
                                    displayEmpty
                                    inputProps={{ 'aria-label': 'Without label' }}
                                    error={submitErrorModel.data.euIsInLineWithValuation}
                                    disabled={!formData?.isFormEditable}
                                >
                                    <MenuItem value="">Select</MenuItem>
                                    <MenuItem value={"Yes"}>Yes</MenuItem>
                                    <MenuItem value={"No"}>No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {valuationSelect === "No" ? <Grid item xs={12} md={6}>
                        <Grid>
                            <label>Valuation Comment</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid>
                            <TextField size="small" fullWidth multiline minRows={1} maxRows={3} value={europeLeaseProjectDetailData.valuationComment}
                                error={submitErrorModel.data.euValuationComment}
                                onChange={(event) => updateField('valuationComment', event.target.value, { euValuationComment: false })}
                                disabled={!formData?.isFormEditable} />
                        </Grid>
                    </Grid> : <Grid item xs={12} md={6} className="grid-hide"></Grid>}

                    <Grid item xs={12}>
                        <Grid>
                            <label>Detailed Lease Description</label><span className="mandatory"> *</span>
                        </Grid>
                        <Grid className={submitErrorModel.data.euDetailedLeaseDescription ? "quill-error" : ""}>
                            <ReactQuill
                                theme="snow"
                                value={europeLeaseProjectDetailData.detailedLeaseDescription ?? ""}
                                onChange={handleDetailedDescriptionChange}
                                readOnly={!formData?.isFormEditable}
                            />
                        </Grid>
                    </Grid>

                    <Grid item md={8}>
                        <label>I certify that an OFAC search has been completed and supporting documentation has been attached</label><span className="mandatory"> *</span>
                        <Checkbox size="small" onChange={handleCheckboxChange} disabled={!formData?.isFormEditable} checked={OFACCheckbox}
                            className={submitErrorModel.data.euOFAC ? `lease-Checkbox` : ``}
                        />
                    </Grid>
                </Grid>
            </>
        } />
    );
};

export default EuropeLeaseProjectDetails;
